import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';

import { Pagination } from '../api/types';

// Page controls for the three action queues (GRAVY-10304).
//
// The queues rendered every qualifying record with no controls at all, so a
// busy agency's tab was a single unbounded list. The server now returns one
// bounded page plus `pagination`, and this renders the position and the two
// steps through it.
//
// Deliberately prev/next rather than numbered pages: `total_pages` is known, but
// the queues are worked front-to-back, so jumping to page 7 is not a thing Ops
// does — and numbered pages would need their own truncation rules for an agency
// with 40 of them.

type Props = {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  /** Distinguishes the three instances for tests and screen readers. */
  queue: string;
};

const QueuePagination = ({ pagination, onPageChange, queue }: Props) => {
  const {
    page,
    per_page: perPage,
    total,
    total_pages: totalPages,
  } = pagination;

  // One page of results needs no controls; an empty queue renders its own empty
  // state instead.
  //
  // EXCEPT when the caller is somehow past the end. `useQueuePageClamp` moves an
  // out-of-range page back and normally that correction lands before anyone sees
  // this, but hiding the controls whenever `total_pages <= 1` is exactly what
  // stranded an admin when a live refresh shrank the queue to one page
  // (GRAVY-10323): no rows, and no Previous to escape with. If the position is
  // impossible, the way back is shown rather than removed.
  const stranded = page > Math.max(1, totalPages);
  if (totalPages <= 1 && !stranded) return null;

  // 1-based, inclusive. `to` is clamped because the last page is short.
  const from = (page - 1) * perPage + 1;
  const to = Math.min(page * perPage, total);

  const atFirst = page <= 1;
  // Past the end this is already true, which is what we want: "next" is
  // meaningless there, while "previous" stays live as the escape route.
  const atLast = page >= totalPages;

  return (
    <nav className="dashv2-qpage" aria-label={`${queue} pagination`}>
      <span className="dashv2-qpage-range">
        {/* An out-of-range page has nothing to show but still reports where it
            thinks it is, so "next" past the end is legible rather than silent. */}
        {from > total ? (
          <>No results on page {page}</>
        ) : (
          <>
            {from}–{to} of {total}
          </>
        )}
      </span>

      <div className="dashv2-qpage-controls">
        <button
          type="button"
          className="dashv2-qpage-btn"
          aria-label="Previous page"
          disabled={atFirst}
          onClick={() => onPageChange(page - 1)}
        >
          <ChevronLeftIcon />
        </button>
        <span className="dashv2-qpage-count" aria-current="page">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          className="dashv2-qpage-btn"
          aria-label="Next page"
          disabled={atLast}
          onClick={() => onPageChange(page + 1)}
        >
          <ChevronRightIcon />
        </button>
      </div>
    </nav>
  );
};

export default QueuePagination;
